import * as React from 'react';
import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from 'embla-carousel-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';
type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: 'horizontal' | 'vertical';
  setApi?: (api: CarouselApi) => void;
};

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
} & CarouselProps;

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

function useCarousel() {
  const context = React.useContext(CarouselContext);

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />');
  }

  return context;
}

function Carousel({
  orientation = 'horizontal',
  opts,
  setApi,
  plugins,
  className,
  children,
  ...props
}: React.ComponentProps<'div'> & CarouselProps) {
  const [carouselRef, api] = useEmblaCarousel(
    {
      ...opts,
      axis: orientation === 'horizontal' ? 'x' : 'y',
    },
    plugins,
  );
  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);

  const onSelect = React.useCallback((api: CarouselApi) => {
    if (!api) return;
    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
  }, []);

  const scrollPrev = React.useCallback(() => {
    api?.scrollPrev();
  }, [api]);

  const scrollNext = React.useCallback(() => {
    api?.scrollNext();
  }, [api]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        scrollPrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        scrollNext();
      }
    },
    [scrollPrev, scrollNext],
  );

  React.useEffect(() => {
    if (!api || !setApi) return;
    setApi(api);
  }, [api, setApi]);

  React.useEffect(() => {
    if (!api) return;
    onSelect(api);
    api.on('reInit', onSelect);
    api.on('select', onSelect);

    return () => {
      api?.off('reInit', onSelect);
      api?.off('select', onSelect);
    };
  }, [api, onSelect]);

  React.useEffect(() => {
    if (!api) return;
    const rootNode = api.rootNode();

    const onWheel = (e: WheelEvent) => {
      const delta =
        Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      if (delta > 0 && api.canScrollNext()) {
        e.preventDefault();
        api.scrollNext();
      } else if (delta < 0 && api.canScrollPrev()) {
        e.preventDefault();
        api.scrollPrev();
      }
    };

    rootNode.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      rootNode.removeEventListener('wheel', onWheel);
    };
  }, [api]);

  return (
    <CarouselContext.Provider
      value={{
        carouselRef,
        api: api,
        opts,
        orientation:
          orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
      }}
    >
      <div
        tabIndex={0}
        onKeyDownCapture={handleKeyDown}
        className={cn('relative outline-none', className)}
        role="region"
        aria-roledescription="carousel"
        data-slot="carousel"
        {...props}
      >
        {children}
      </div>
    </CarouselContext.Provider>
  );
}

function CarouselContent({ className, ...props }: React.ComponentProps<'div'>) {
  const { carouselRef, orientation } = useCarousel();

  return (
    <div
      ref={carouselRef}
      className="overflow-hidden @container"
      data-slot="carousel-content"
    >
      <div
        className={cn(
          'flex justify-start',
          orientation === 'horizontal' ? '-ml-4' : '-mt-4 flex-col',
          className,
        )}
        {...props}
      />
    </div>
  );
}

function CarouselItem({ className, ...props }: React.ComponentProps<'div'>) {
  const { orientation } = useCarousel();

  return (
    <div
      role="group"
      aria-roledescription="slide"
      data-slot="carousel-item"
      className={cn(
        'min-w-0 shrink-0 grow-0 basis-full',
        orientation === 'horizontal' ? 'pl-4' : 'pt-4',
        className,
      )}
      {...props}
    />
  );
}

function CarouselPrevious({
  className,
  variant = 'icon',
  size = 'icon-lg',
  ...props
}: React.ComponentProps<typeof Button>) {
  const { orientation, scrollPrev, canScrollPrev } = useCarousel();

  return (
    <Button
      data-slot="carousel-previous"
      variant={variant}
      size={size}
      className={cn(
        'rounded-1.5xl absolute touch-manipulation cursor-pointer border border-gray-200 bg-white shadow-[0px_1.7px_7px_0px_rgba(8,15,54,0.05)] p-2',
        orientation === 'horizontal'
          ? 'nsm:top-[45%] top-[40%] nsm:-left-7 left-2 -translate-y-1/2'
          : '-top-12 left-1/2 -translate-x-1/2 rotate-90',
        !canScrollPrev && 'hidden',
        className,
      )}
      onClick={scrollPrev}
      {...props}
    >
      <LuChevronLeft className="size-6 shrink-0" />
      <span className="sr-only">Précédent</span>
    </Button>
  );
}

function CarouselNext({
  className,
  variant = 'icon',
  size = 'icon-lg',
  ...props
}: React.ComponentProps<typeof Button>) {
  const { orientation, scrollNext, canScrollNext } = useCarousel();

  return (
    <Button
      data-slot="carousel-next"
      variant={variant}
      size={size}
      className={cn(
        'absolute cursor-pointer border border-gray-200 bg-white shadow-[0px_1.7px_7px_0px_rgba(8,15,54,0.05)] rounded-1.5xl p-2',
        orientation === 'horizontal'
          ? 'nsm:top-[45%] top-[40%] nsm:-right-7 right-2 -translate-y-1/2'
          : '-bottom-12 left-1/2 -translate-x-1/2 rotate-90',
        !canScrollNext && 'hidden',
        className,
      )}
      onClick={scrollNext}
      {...props}
    >
      <LuChevronRight className="size-6 shrink-0" />
      <span className="sr-only">Suivant</span>
    </Button>
  );
}

type CarouselDotsProps = {
  /** Number of slides (dots). When the carousel has a leading spacer, this is the count of real items. */
  count: number;
  /** Index of the first "real" slide in Embla (0 = no spacer, 1 = one leading spacer). Default 1. */
  scrollSnapOffset?: number;
};

function CarouselDots({ count, scrollSnapOffset = 1 }: CarouselDotsProps) {
  const { api } = useCarousel();
  const [selectedIndex, setSelectedIndex] = React.useState(0);

  React.useEffect(() => {
    if (!api) return;
    const onSelect = () => {
      const snap = api.selectedScrollSnap();
      const dotIndex = Math.max(
        0,
        Math.min(snap - scrollSnapOffset, count - 1),
      );
      setSelectedIndex(dotIndex);
    };
    onSelect();
    api.on('select', onSelect);
    return () => {
      api.off('select', onSelect);
    };
  }, [api, count, scrollSnapOffset]);

  if (count === 0) return null;

  return (
    <div
      className="flex gap-4 items-center justify-center w-full"
      role="tablist"
      aria-label="Position dans le carousel"
    >
      {Array.from({ length: count }, (_, i) => (
        <div
          key={i}
          role="tab"
          aria-selected={i === selectedIndex}
          aria-label={`Slide ${i + 1} sur ${count}`}
          className={cn(
            'size-2 shrink-0 rounded-full transition-colors',
            i === selectedIndex ? 'bg-indigo-900' : 'bg-gray-300',
          )}
        />
      ))}
    </div>
  );
}

export {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  CarouselDots,
  useCarousel,
};
